import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { verifyEmail, resendVerification, readApiError } from '@/lib/auth-api';

type State = 'verifying' | 'success' | 'error' | 'missing';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState<State>(token ? 'verifying' : 'missing');
  const [error, setError] = useState<string | null>(null);

  // Verification tokens are single-use: the backend nulls the token the moment
  // it's consumed. React 18 StrictMode runs effects twice in dev, which would
  // fire a second verify with the now-consumed token and surface a spurious
  // "invalid or expired" error. The ref survives StrictMode's double-invoke
  // (same component instance), so we only ever hit the endpoint once per token.
  const verifiedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token) return;
    // The ref guard (not a per-effect `cancelled` flag) is what dedupes here.
    // A `cancelled` flag would be tripped by StrictMode's cleanup between the
    // two invokes and suppress the lone in-flight call's result, stranding the
    // page on "verifying". After the double-invoke settles the component is
    // mounted, so setting state when the request resolves is safe.
    if (verifiedToken.current === token) return;
    verifiedToken.current = token;
    (async () => {
      try {
        await verifyEmail(token);
        await qc.invalidateQueries({ queryKey: ['currentUser'] });
        setState('success');
        setTimeout(() => nav('/app', { replace: true }), 1500);
      } catch (err) {
        setError(readApiError(err, t('auth.verify.errorBody')));
        setState('error');
      }
    })();
  }, [token, qc, nav, t]);

  if (state === 'verifying') {
    return (
      <AuthLayout title={t('auth.verify.verifyingTitle')} subtitle={t('auth.verify.verifyingBody')}>
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <span className="h-5 w-5 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
        </div>
      </AuthLayout>
    );
  }

  if (state === 'success') {
    return (
      <AuthLayout title={t('auth.verify.successTitle')} subtitle={t('auth.verify.successBody')}>
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-700 dark:text-emerald-300">{t('auth.verify.redirecting')}</p>
        </div>
      </AuthLayout>
    );
  }

  if (state === 'missing') {
    return (
      <AuthLayout title={t('auth.verify.missingTitle')} subtitle={t('auth.verify.missingBody')}>
        <CheckYourInbox />
      </AuthLayout>
    );
  }

  // error
  return (
    <AuthLayout title={t('auth.verify.errorTitle')} subtitle={t('auth.verify.errorBody')}>
      <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 flex items-start gap-3 mb-4">
        <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
      <CheckYourInbox />
    </AuthLayout>
  );
}

function CheckYourInbox() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
          <Mail className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm font-semibold">{t('auth.verify.checkInbox')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('auth.verify.checkInboxHint')}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={async () => {
          setBusy(true);
          try {
            await resendVerification();
            setDone(true);
          } catch {
            // The endpoint is idempotent and silently no-ops if already
            // verified; we don't surface the error.
          } finally {
            setBusy(false);
          }
        }}
        disabled={busy || done}
        className="inline-flex h-9 items-center justify-center px-3 rounded-lg border border-border text-xs font-semibold hover:bg-muted disabled:opacity-60 transition"
      >
        {done ? t('auth.verify.resent') : busy ? t('auth.verify.resending') : t('auth.verify.resend')}
      </button>
      <p className="text-xs text-muted-foreground">
        <Link to="/login" className="font-semibold text-primary hover:underline">{t('auth.verify.backToLogin')}</Link>
      </p>
    </div>
  );
}
